import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AuthContext, AuthenticationActions } from '@next/core-lib/authentication';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { CartActions, CartLineSources } from '@next/core-lib/cart';
import {
  DaterangeConfig,
  IdentifierTriggerComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { ExportFormat, ExportComponent } from '@next/core-lib/export';
import { FeatureFlags, FeatureFlagService } from '@next/core-lib/featureflag';
import { LocaleService, TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';

import { SessionActions, SessionSelectors, UserSession } from '@next/core-lib/session';
import { SettingsActions, SettingsStorage } from '@next/core-lib/settings';
import { TableColumnTypes } from '@next/core-lib/table';
import {
  ExportOptionsV2,
  TableComponent,
  TableSearchTemplateDirective,
  TableSearchComponent,
  TableFiltersTemplateDirective,
  TableDateRangeFilterComponent,
  TableMultiSelectFilterComponent,
  TableTopBarTemplateDirective,
  TableTopBarComponent,
  TableSelectedItemsComponent,
  TableExportDirective,
} from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, race, timer } from 'rxjs';
import { catchError, finalize, map, switchMap, tap, take } from 'rxjs/operators';
import { getInvoiceDocumentRequest } from 'src/app/invoice-data/store/invoice-data.actions';
import { Environment } from 'src/environments/environment.model';

import { DeliveryColumnTypes } from '../../../delivery-data/delivery-data.model';
import { DeliveryDataService } from '../../../delivery-data/services/delivery-data.service';
import {
  getDeliveryDocumentRequest,
  getMSDSRequest,
  getQualityCertificateRequest,
} from '../../../delivery-data/store/delivery-data.actions';
import { createLocationOptions } from '../../../shared/data/location-options.mapper';
import { settingsContextName, SettingsKeys } from '../../../shared/models/shared.models';
import { TableActionsButtonComponent } from '../../../shared/table-actions-button/table-actions-button.component';
import { DeliveryOverviewDataSource } from '../../datasource/deliveries-overview.datasource';
import {
  deliveryColumns,
  DeliveryOverviewDataFilters,
  DeliveryOverviewViewModel,
} from '../../models/delivery.model';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-delivery-overview',
  templateUrl: './delivery-overview.component.html',
  styleUrls: ['./delivery-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    TableComponent,
    MatSort,
    TableSearchTemplateDirective,
    ReactiveFormsModule,
    TableSearchComponent,
    TableFiltersTemplateDirective,
    TableDateRangeFilterComponent,
    TableMultiSelectFilterComponent,
    TableTopBarTemplateDirective,
    TableTopBarComponent,
    TableSelectedItemsComponent,
    ExportComponent,
    TableExportDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    RouterLink,
    IdentifierTriggerComponent,
    TableActionsButtonComponent,
    MatMenuItem,
    MatIcon,
    MatButton,
    MatIconButton,
    MatMenu,
    MatMenuTrigger,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    NotificationEmptyComponent,
    AsyncPipe,
    MatProgressSpinner,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class DeliveryOverviewComponent implements OnInit {
  private static readonly importCooldownMs = 10000;

  public readonly environment = inject<Environment>(EnvironmentInjector);
  private readonly destroyRef = inject(DestroyRef);
  readonly dataSource = new DeliveryOverviewDataSource(this.deliveryDataService);
  readonly displayColumns = [...deliveryColumns, 'actions'];

  readonly userSession$ = this.store.select(SessionSelectors.selectUserSession);
  userSession: UserSession = {} as UserSession;

  readonly searchAndFilters = new FormGroup({
    search: new FormControl(),
    locations: new FormControl(),
    dateRange: new FormControl(),
  });

  readonly hasCustomerRole$ = this.store.select(AuthorizationSelectors.hasCustomerRole);
  readonly isImporting = signal(false);

  readonly deliveryLocationOptions$ = createLocationOptions(
    this.store.select(UserSelectors.selectLocations),
    this.searchAndFilters.controls.dateRange.valueChanges,
    this.searchAndFilters.controls.dateRange.value ?? new DaterangeConfig().config.initial,
    (startDate, endDate) => this.deliveryDataService.getDeliveryShipTos(startDate, endDate),
  );

  readonly exportOptions: ExportOptionsV2;
  returnOrdersEnabled$: Observable<boolean>;

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly deliveryDataService: DeliveryDataService,
    private readonly actionsListener$: ActionsSubject,
    private featureFlagService: FeatureFlagService,
  ) {
    this.exportOptions = {
      formats: [
        {
          format: ExportFormat.xlsx,
          name: 'XLSX',
        },
        {
          format: ExportFormat.csv,
          name: 'CSV',
        },
        {
          name: 'PDF',
          export: (data: DeliveryOverviewViewModel[]) => this.downloadPdf(data),
        },
      ],
      fileName: this.localeService.translate('delivery.delivery-overview.fileName'),
      columns: deliveryColumns.map((col) => ({
        id: col,
        name: this.localeService.translate(`delivery.delivery-overview.${col}`),
      })),
    };
  }

  ngOnInit(): void {
    this.userSession$.subscribe((userSession: UserSession) => (this.userSession = userSession));

    this.searchAndFilters.valueChanges.subscribe(() =>
      this.dataSource.applyFilters(this.searchAndFilters.value as DeliveryOverviewDataFilters),
    );

    const salesOrganization$ = this.store
      .select(UserSelectors.selectCustomer)
      .pipe(
        map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
      );

    const returFfEnabled$ = salesOrganization$.pipe(
      switchMap((salesArea) =>
        this.featureFlagService.isSalesAreaFeatureFlagEnabled(
          salesArea,
          FeatureFlags.returnOrdersEnabled,
        ),
      ),
    );

    this.returnOrdersEnabled$ = combineLatest(
      [this.hasCustomerRole$, returFfEnabled$],
      (hasCustomerRole, returnEnabled) => !hasCustomerRole && returnEnabled,
    );
  }

  isEqual(a: DeliveryOverviewViewModel, b: DeliveryOverviewViewModel) {
    return a.id === b.id && a.deliveryId === b.deliveryId;
  }

  resetFilters() {
    this.searchAndFilters.reset({
      search: null,
      locations: null,
      dateRange: new DaterangeConfig().config.initial,
    });
  }

  getTableColumnType(dCol: DeliveryColumnTypes) {
    return dCol === 'deliveryDate'
      ? TableColumnTypes.DATE
      : ['unitsDelivered', 'baseQuantity'].includes(dCol)
        ? TableColumnTypes.NUMBER
        : TableColumnTypes.STRING;
  }

  public openMSDS(delivery: DeliveryOverviewViewModel) {
    return this.store.dispatch(
      getMSDSRequest({
        shipToId: delivery.shipTo.id,
        externalProductId: delivery.externalProductId,
      }),
    );
  }

  public showInvoice(searchValue = ''): void {
    this.store.dispatch(
      SettingsActions.setSettings({
        settings: {
          contextName: settingsContextName,
          storage: SettingsStorage.PlatformSession,
          key: SettingsKeys.search,
          data: searchValue,
        },
      }),
    );
  }

  public downloadPdf(selectedDeliveries: DeliveryOverviewViewModel[]) {
    if (selectedDeliveries.length > 0) {
      this.store.dispatch(
        getDeliveryDocumentRequest({
          ids: selectedDeliveries.map((delivery) => delivery.deliveryId),
          shipToIds: selectedDeliveries.map((delivery) => delivery.shipTo.id),
        }),
      );
    } else {
      this.store.dispatch(
        NotificationActions.warningSnackbar({
          message: this.localeService.translate(
            'delivery.delivery-overview.no-selection-for-pdf-download-warning',
          ),
        }),
      );
    }
  }

  public importDeliveries(): void {
    if (this.isImporting()) {
      return;
    }

    this.isImporting.set(true);
    this.deliveryDataService
      .importDeliveries()
      .pipe(
        take(1),
        switchMap(() => {
          this.store.dispatch(
            NotificationActions.successSnackbar({
              message: this.localeService.translate('delivery.import-requested'),
            }),
          );
          this.refreshDeliveries();
          return timer(DeliveryOverviewComponent.importCooldownMs);
        }),
        tap(() => this.refreshDeliveries()),
        catchError(() => {
          this.store.dispatch(
            NotificationActions.errorSnackbar({
              message: this.localeService.translate('delivery.import-failed'),
            }),
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isImporting.set(false)),
      )
      .subscribe();
  }

  private refreshDeliveries(): void {
    this.dataSource.applyFilters(this.searchAndFilters.value as DeliveryOverviewDataFilters);
  }

  public downloadInvoice(delivery: DeliveryOverviewViewModel) {
    this.store.dispatch(
      getInvoiceDocumentRequest({
        ids: [delivery.invoiceId],
      }),
    );
  }

  public downloadDelivery(delivery: DeliveryOverviewViewModel) {
    this.store.dispatch(
      getDeliveryDocumentRequest({
        ids: [delivery.deliveryId],
        shipToIds: [delivery.shipTo.id],
      }),
    );
  }

  public downloadQualityCertificate(delivery: DeliveryOverviewViewModel) {
    this.store.dispatch(
      getQualityCertificateRequest({
        itemId: delivery.itemId as string,
        shipToId: delivery.shipTo.id,
        itemVLDoctype: delivery.itemVLDoctype as string,
        system: delivery.externalSystem as string,
      }),
    );
  }

  public addSkuToReturnOrder(delivery: DeliveryOverviewViewModel) {
    // Set up correct session variables in order to send the locationId as a header in the HTTP request.
    this.setSessionVariablesForAddSkuToReturnOrder(
      AuthContext.CustomerLocation,
      delivery.shipTo.id,
    );

    this.sendAddLineToReturnCartAction(delivery);

    // Either redirect to the cart site when the return order was successfully added,
    // or 'reset' the session variables and stay on the my-site.
    this.getAddSkuToReturnOrderResult().subscribe((result) => {
      if (result.type === CartActions.postCartSuccess.type) {
        this.redirectToCartSite();
      } else {
        this.setSessionVariablesForAddSkuToReturnOrder(AuthContext.Customer, null);
      }
    });
  }

  private setSessionVariablesForAddSkuToReturnOrder(
    authContext: AuthContext,
    deliveryShipToId: number | null,
  ) {
    this.store.dispatch(
      AuthenticationActions.setAuthContext({
        context: authContext,
      }),
    );

    this.store.dispatch(
      SessionActions.setUserSession({
        data: { ...this.userSession, locationId: deliveryShipToId },
      }),
    );
  }

  private sendAddLineToReturnCartAction(delivery: DeliveryOverviewViewModel) {
    this.store.dispatch(
      CartActions.addLineToReturnCart({
        cartLine: {
          skuId: delivery.skuId,
          quantity: Number.parseFloat(delivery.unitsDelivered) || 0,
          externalInvoiceId: delivery.externalInvoiceId,
          externalInvoiceLineId: delivery.invoiceLineId,
          source: CartLineSources.MYSITE_RETURN_ORDER,
        },
      }),
    );
  }

  private getAddSkuToReturnOrderResult() {
    return race(
      [CartActions.postCartSuccess, NotificationActions.errorSnackbar].map((action) =>
        this.actionsListener$.pipe(ofType(action), take(1)),
      ),
    );
  }

  private redirectToCartSite() {
    const cartUrl = `${this.environment.SHOP_URL}/cart`;
    window.location.href = cartUrl;
  }
}
